/**
 * 저장하지 않고 이동 시 확인용 전역 Store (Zustand)
 * - shared 계층에 두어 FSD 의존성 준수 (shared ← entities 참조 방지)
 * - 문서 양식 관리 / 결재라인 안내 관리 / 양식 추가·편집에서 변경사항이 있을 때
 *   목록(취소), LNB 메뉴, 로그아웃 클릭 시 확인 모달을 띄우고
 *   "예" 선택 시 작성 데이터 초기화 후 요청한 동작 수행
 */

import { create } from 'zustand';

const UNSAVED_SCOPE = {
  DOCUMENT_FORMS: 'documentForms',
  APPROVAL_LINE_GUIDE: 'approvalLineGuide',
  /** 양식 추가/편집 화면 (목록, LNB, 로그아웃) */
  FORM_ADD_EDIT: 'formAddEdit',
  /** 직원관리 (리스트 선택/직원 추가/LNB/로그아웃) */
  EMPLOYEE_MANAGEMENT: 'employeeManagement',
};

/**
 * @typedef {typeof UNSAVED_SCOPE[keyof typeof UNSAVED_SCOPE] | null} UnsavedScope
 */

/**
 * getResetAndProceed: () => (next: () => void) => void
 * - 초기화 후 next(이동/로그아웃) 실행
 */

/**
 * @param {Object} set - Zustand set
 * @returns {Object} store
 */
function createUnsavedLeaveStore(set) {
  return {
    scope: null,
    /** @type {(() => (next: () => void) => void) | null} */
    getResetAndProceed: null,
    /** @type {(() => void) | null} - 확인 "예" 시 실행할 이동/로그아웃 콜백 */
    pendingLeave: null,

    /**
     * unsaved 상태 등록 (해당 페이지에서 변경사항이 있을 때 호출)
     * @param {UnsavedScope} scope - 'documentForms' | 'approvalLineGuide' | 'formAddEdit' | 'employeeManagement'
     * @param {() => (next: () => void) => void} getResetAndProceed - 초기화 후 next 호출하는 함수 반환
     */
    setUnsaved(scope, getResetAndProceed) {
      set({ scope, getResetAndProceed });
    },

    /**
     * unsaved 상태 해제 (저장 완료 또는 초기화 후)
     */
    clearUnsaved() {
      set({ scope: null, getResetAndProceed: null });
    },

    /**
     * 이동/로그아웃 시도 시 호출. scope가 없으면 즉시 nextAction 실행, 있으면 확인 모달용으로 pendingLeave에 저장
     * @param {() => void} nextAction - 확인 "예" 후 실행할 동작 (navigate 또는 logout)
     */
    requestLeave(nextAction) {
      const state = useUnsavedLeaveStore.getState();
      if (!state.scope || !state.getResetAndProceed) {
        nextAction();
        return;
      }
      set({ pendingLeave: nextAction });
    },

    /**
     * 확인 "예" 클릭 시: 초기화 후 pendingLeave 실행, 상태 클리어
     */
    confirmLeave() {
      const state = useUnsavedLeaveStore.getState();
      const next = state.pendingLeave;
      const getResetAndProceed = state.getResetAndProceed;
      set({ pendingLeave: null, scope: null, getResetAndProceed: null });
      if (getResetAndProceed && typeof next === 'function') {
        getResetAndProceed()(next);
      }
    },

    /**
     * 확인 "아니오" 클릭 시: pendingLeave만 클리어
     */
    cancelLeave() {
      set({ pendingLeave: null });
    },
  };
}

const useUnsavedLeaveStore = create(createUnsavedLeaveStore);

export default useUnsavedLeaveStore;
export { UNSAVED_SCOPE };
